import { useCallback, useEffect, useState } from 'react';
import type { BookingType } from '@makemypartner/shared';
import { apiFetch } from '@/lib/api/client';

/**
 * The admin view of the service catalogue.
 *
 * Separate from `lib/data/services.ts` on purpose. That one reads Supabase
 * directly under RLS and only ever sees `is_active = true` categories — which
 * is exactly right for browse and for the companion picker. Admin needs the
 * inactive ones too, plus the listing counts, and writes have to go through
 * the API because the table is SELECT-only under RLS for every role.
 *
 * Two audiences, two shapes, two access paths. Merging them would mean one
 * hook that behaves differently depending on who is signed in.
 */

export interface AdminCategory {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  guidance: string | null;
  allowed_booking_types: BookingType[];
  icon_key: string;
  hue: number;
  requires_background_check: boolean;
  is_active: boolean;
  sort_order: number;
  /** Companions currently offering this. The cost of deactivating it. */
  listing_count: number;
}

export interface CategoryDraft {
  slug: string;
  name: string;
  description: string;
  guidance: string;
  allowedBookingTypes: BookingType[];
  iconKey: string;
  hue: number;
  requiresBackgroundCheck: boolean;
  sortOrder: number;
}

export function useAdminCategories() {
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      const res = await apiFetch<{ categories: AdminCategory[] }>('/api/admin/service-categories');
      setCategories(res.categories);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load services.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { categories, loading, error, reload };
}

export async function createCategory(draft: CategoryDraft): Promise<AdminCategory> {
  const res = await apiFetch<{ category: AdminCategory }>('/api/admin/service-categories', {
    method: 'POST',
    body: draft,
  });
  return res.category;
}

export async function updateCategory(
  id: string,
  patch: Partial<Omit<CategoryDraft, 'slug'>> & { isActive?: boolean },
): Promise<AdminCategory> {
  const res = await apiFetch<{ category: AdminCategory }>(
    `/api/admin/service-categories/${id}`,
    { method: 'PATCH', body: patch },
  );
  return res.category;
}

/**
 * `Dining Partner` → `dining-partner`.
 *
 * Only ever used to PREFILL the slug field while typing a new name, never to
 * silently rewrite one. The slug is permanent once saved, so the admin sees it
 * and can correct it before it becomes a URL somebody bookmarks.
 */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}
